import { useLanguage } from '../contexts/LanguageContext';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

export default function Privacy() {
  const { language } = useLanguage();
  const isPt = language === 'pt';

  return (
    <div data-testid="privacy-page" className="pt-20 min-h-screen bg-background-light">
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-4xl">
          <Link to="/">
            <Button variant="ghost" className="mb-6 text-primary-deep hover:text-accent-blue p-0">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isPt ? 'Voltar ao início' : 'Back to home'}
            </Button>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary-deep/10 flex items-center justify-center">
              <Shield className="w-7 h-7 text-primary-deep" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-dark">
                {isPt ? 'Política de Privacidade' : 'Privacy Policy'}
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                {isPt ? 'Última atualização: Janeiro 2025' : 'Last updated: January 2025'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12 space-y-10">
            
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-dark mb-4">
                {isPt ? '1. Responsável pelo Tratamento' : '1. Data Controller'}
              </h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {isPt 
                  ? 'Miguel Andrade – Fisioterapia & Reabilitação\nEdifício Atlântico, Rua Dr. Eduardo Burnay, Sala 12\n2655-368 Ericeira, Portugal\n\nContacto: info@diogoh.pt | +351 911 739 498'
                  : 'Miguel Andrade – Fisioterapia & Reabilitação\nEdifício Atlântico, Rua Dr. Eduardo Burnay, Sala 12\n2655-368 Ericeira, Portugal\n\nContact: info@diogoh.pt | +351 911 739 498'}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-dark mb-4">
                {isPt ? '2. Dados que Recolhemos' : '2. Data We Collect'}
              </h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {isPt 
                  ? 'Dados de identificação: Nome, email, telefone\n\nDados de saúde (apenas em consultas): Histórico clínico, informações sobre lesões, notas de tratamento\n\nDados de navegação: Endereço IP, tipo de navegador, páginas visitadas, cookies'
                  : 'Identification data: Name, email, phone\n\nHealth data (consultations only): Medical history, injury information, treatment notes\n\nBrowsing data: IP address, browser type, pages visited, cookies'}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-dark mb-4">
                {isPt ? '3. Finalidades do Tratamento' : '3. Processing Purposes'}
              </h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {isPt 
                  ? '• Prestação de serviços de fisioterapia\n• Agendamento e gestão de consultas\n• Comunicação sobre tratamentos\n• Cumprimento de obrigações legais\n• Melhoria da experiência no website'
                  : '• Provision of physiotherapy services\n• Scheduling and managing appointments\n• Communication about treatments\n• Compliance with legal obligations\n• Improving website experience'}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-dark mb-4">
                {isPt ? '4. Base Legal (RGPD Art. 6.º)' : '4. Legal Basis (GDPR Art. 6)'}
              </h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {isPt 
                  ? '• Execução de contrato: Prestação de serviços de fisioterapia\n• Consentimento: Comunicações de marketing e cookies não essenciais\n• Obrigação legal: Cumprimento de obrigações fiscais\n• Interesse legítimo: Segurança do website\n\nPara dados de saúde: RGPD Art. 9.º, n.º 2, alínea h)'
                  : '• Contract performance: Provision of physiotherapy services\n• Consent: Marketing communications and non-essential cookies\n• Legal obligation: Compliance with tax obligations\n• Legitimate interest: Website security\n\nFor health data: GDPR Art. 9(2)(h)'}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-dark mb-4">
                {isPt ? '5. Conservação dos Dados' : '5. Data Retention'}
              </h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {isPt 
                  ? '• Dados de contacto: 2 anos após última interação\n• Registos clínicos: 5 anos após última consulta\n• Dados fiscais: 10 anos\n• Dados de navegação: Conforme Política de Cookies'
                  : '• Contact data: 2 years after last interaction\n• Clinical records: 5 years after last consultation\n• Tax data: 10 years\n• Browsing data: As per Cookie Policy'}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-dark mb-4">
                {isPt ? '6. Os Seus Direitos' : '6. Your Rights'}
              </h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {isPt 
                  ? 'Ao abrigo do RGPD, tem direito a:\n• Acesso aos seus dados\n• Retificação de dados incorretos\n• Apagamento dos dados\n• Limitação do tratamento\n• Portabilidade dos dados\n• Oposição ao tratamento\n• Retirar o consentimento\n\nContacte-nos: info@diogoh.pt | +351 911 739 498'
                  : 'Under the GDPR, you have the right to:\n• Access your data\n• Rectify incorrect data\n• Erasure of data\n• Restriction of processing\n• Data portability\n• Object to processing\n• Withdraw consent\n\nContact us: info@diogoh.pt | +351 911 739 498'}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-dark mb-4">
                {isPt ? '7. Reclamações' : '7. Complaints'}
              </h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                {isPt 
                  ? 'Pode apresentar reclamação junto da:\n\nComissão Nacional de Proteção de Dados (CNPD)\nAv. D. Carlos I, 134 - 1.º\n1200-651 Lisboa\nTel: +351 213 928 400\nwww.cnpd.pt'
                  : 'You can lodge a complaint with:\n\nComissão Nacional de Proteção de Dados (CNPD)\nAv. D. Carlos I, 134 - 1.º\n1200-651 Lisboa\nTel: +351 213 928 400\nwww.cnpd.pt'}
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
